import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import { Formik, Form as FormikForm, Field } from 'formik'
import * as Yup from 'yup'
import {
  FaTruck,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaSatellite,
  FaIdCard,
  FaCalendarAlt,
  FaSearch,
  FaCar,
  FaUpload,
} from 'react-icons/fa'
import {
  fetchTrucks,
  fetchTruck,
  addTruck,
  updateTruck,
  deleteTruck,
  clearSelectedTruck,
} from '../../store/slices/truckSlice'
import '../../assets/scss/pages/TruckManagement.scss'

const TruckSchema = Yup.object().shape({
  truckNumber: Yup.string().required('Truck number is required'),
  chasisNumber: Yup.string().required('Chassis number is required'),
  engineNumber: Yup.string().required('Engine number is required'),
  registrationValidUpto: Yup.date().required('Validity date is required'),
  fitnessValidFrom: Yup.date().nullable(),
  fitnessValidTo: Yup.date().nullable(),
  insuranceValidFrom: Yup.date().nullable(),
  insuranceValidTo: Yup.date().nullable(),
  pucValidFrom: Yup.date().nullable(),
  pucValidTo: Yup.date().nullable(),
  isGpsInstalled: Yup.string().required('GPS status is required'),
})

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: i => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' } }),
}

const TruckManagement = () => {
  const dispatch = useDispatch()
  const { trucks, selectedTruck, loading } = useSelector(state => state.truck)

  const [showModal, setShowModal]         = useState(false)
  const [editingTruck, setEditingTruck]   = useState(null)
  const [viewTruck, setViewTruck]         = useState(null)
  const [deleteModal, setDeleteModal]     = useState(false)
  const [truckToDelete, setTruckToDelete] = useState(null)
  const [statusFilter, setStatusFilter]   = useState('all')
  const [search, setSearch]               = useState('')

  useEffect(() => { dispatch(fetchTrucks()) }, [dispatch])

  useEffect(() => {
    if (selectedTruck) setViewTruck(selectedTruck)
  }, [selectedTruck])

  const initialValues = useMemo(() => ({
    truckNumber:           editingTruck?.truckNumber || '',
    chasisNumber:          editingTruck?.chasisNumber || '',
    engineNumber:          editingTruck?.engineNumber || '',
    registrationValidUpto: editingTruck?.registrationValidUpto ? editingTruck.registrationValidUpto.split('T')[0] : '',
    fitnessValidFrom:      editingTruck?.fitnessValidFrom ? editingTruck.fitnessValidFrom.split('T')[0] : '',
    fitnessValidTo:        editingTruck?.fitnessValidTo ? editingTruck.fitnessValidTo.split('T')[0] : '',
    insuranceValidFrom:    editingTruck?.insuranceValidFrom ? editingTruck.insuranceValidFrom.split('T')[0] : '',
    insuranceValidTo:      editingTruck?.insuranceValidTo ? editingTruck.insuranceValidTo.split('T')[0] : '',
    pucValidFrom:          editingTruck?.pucValidFrom ? editingTruck.pucValidFrom.split('T')[0] : '',
    pucValidTo:            editingTruck?.pucValidTo ? editingTruck.pucValidTo.split('T')[0] : '',
    isGpsInstalled:        editingTruck?.isGpsInstalled ? 'yes' : 'no',
    fileRC:                null,
    fileFitness:           null,
    filePermit:            null,
    fileInsurance:         null,
    filePUC:               null,
    fileTDS:               null,
  }), [editingTruck])

  const handleCloseModal     = () => { setShowModal(false); setEditingTruck(null) }
  const handleShowViewModal  = (truck) => dispatch(fetchTruck(truck.id))
  const handleCloseViewModal = () => { setViewTruck(null); dispatch(clearSelectedTruck()) }

  const confirmDelete = async () => {
    if (truckToDelete) {
      await dispatch(deleteTruck(truckToDelete.id)).unwrap()
      setDeleteModal(false)
      setTruckToDelete(null)
    }
  }

  const filteredTrucks = useMemo(() => {
    let list = trucks
    if (statusFilter === 'active')   list = list.filter(t => t.isActive)
    if (statusFilter === 'inactive') list = list.filter(t => !t.isActive)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(t =>
        t.truckNumber?.toLowerCase().includes(q) ||
        t.chasisNumber?.toLowerCase().includes(q) ||
        t.engineNumber?.toLowerCase().includes(q)
      )
    }
    return list
  }, [trucks, statusFilter, search])

  const totalActive   = trucks.filter(t => t.isActive).length
  const totalInactive = trucks.length - totalActive
  const gpsCount      = trucks.filter(t => t.isGpsInstalled).length

  return (
    <div className="tm-page">

      {/* HEADER */}
      <motion.div custom={0} variants={fade} initial="hidden" animate="visible" className="tm-header">
        <div className="tm-header-left">
          <div className="tm-header-icon"><FaTruck /></div>
          <div>
            <h1 className="tm-title">Truck Management</h1>
            <p className="tm-sub">Manage your fleet, documents and compliance records</p>
          </div>
        </div>
        <button
          className="tm-add-btn"
          onClick={() => { setEditingTruck(null); setShowModal(true) }}
        >
          <FaPlus /> Add Truck
        </button>
      </motion.div>

      {/* KPI SUMMARY */}
      <Row className="g-3 mb-3">
        {[
          { label: 'Total Fleet',  value: trucks.length, note: 'registered',   icon: <FaTruck />,       color: 'blue'   },
          { label: 'Active',       value: totalActive,   note: 'operational',  icon: <FaCheckCircle />, color: 'green'  },
          { label: 'GPS Enabled',  value: gpsCount,      note: 'with tracker', icon: <FaSatellite />,   color: 'purple' },
        ].map((k, i) => (
          <Col key={k.label} xs={12} md={4}>
            <motion.div custom={i + 1} variants={fade} initial="hidden" animate="visible" className={`tm-kpi tm-kpi--${k.color}`}>
              <div className="tm-kpi-left">
                <div className="tm-kpi-val">{k.value}</div>
                <div className="tm-kpi-label">{k.label}</div>
                <div className="tm-kpi-note">{k.note}</div>
              </div>
              <div className="tm-kpi-icon">{k.icon}</div>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* FILTER BAR */}
      <motion.div custom={4} variants={fade} initial="hidden" animate="visible" className="tm-filter-bar">
        <div className="tm-search-wrap">
          <FaSearch className="tm-search-icon" />
          <input
            className="tm-search"
            placeholder="Search by truck no, chassis or engine…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="tm-filter-tabs">
          {['all', 'active', 'inactive'].map(f => (
            <button
              key={f}
              className={`tm-tab ${statusFilter === f ? 'tm-tab--active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <span className="tm-count">{filteredTrucks.length} trucks</span>
      </motion.div>

      {/* TRUCK LIST */}
      {loading ? (
        <div className="tm-loading">
          <Spinner animation="border" style={{ color: 'rgb(20,35,115)' }} />
          <span>Loading fleet…</span>
        </div>
      ) : filteredTrucks.length === 0 ? (
        <motion.div custom={5} variants={fade} initial="hidden" animate="visible" className="tm-empty">
          <FaTruck className="tm-empty-icon" />
          <p className="tm-empty-title">No trucks found</p>
          <p className="tm-empty-sub">Add your first truck or adjust the filter above.</p>
        </motion.div>
      ) : (
        <Row className="g-3">
          <AnimatePresence>
            {filteredTrucks.map((truck, i) => (
              <Col key={truck.id} xs={12} sm={6} md={4} xl={3}>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ delay: i * 0.04, duration: 0.28 }}
                  className="tm-card"
                >
                  <div className="tm-card-accent" />
                  <div className="tm-card-head">
                    <div className="tm-card-avatar"><FaTruck /></div>
                    <div className="tm-card-meta">
                      <div className="tm-card-name">{truck.truckNumber}</div>
                      <span className={`tm-badge ${truck.isActive ? 'tm-badge--active' : 'tm-badge--inactive'}`}>
                        {truck.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="tm-card-details">
                    <div className="tm-card-row">
                      <FaIdCard className="tm-card-icon" />
                      <span className="tm-card-val tm-card-mono">{truck.chasisNumber || '—'}</span>
                    </div>
                    <div className="tm-card-row">
                      <FaCar className="tm-card-icon" />
                      <span className="tm-card-val tm-card-mono">{truck.engineNumber || '—'}</span>
                    </div>
                    <div className="tm-card-row">
                      <FaSatellite className="tm-card-icon" />
                      <span className="tm-card-val">
                        <span className={`tm-badge ${truck.isGpsInstalled ? 'tm-badge--gps' : 'tm-badge--no-gps'}`}>
                          {truck.isGpsInstalled ? 'GPS On' : 'No GPS'}
                        </span>
                      </span>
                    </div>
                    {truck.registrationValidUpto && (
                      <div className="tm-card-row">
                        <FaCalendarAlt className="tm-card-icon" />
                        <span className="tm-card-val">Reg. valid: {new Date(truck.registrationValidUpto).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                  <div className="tm-card-footer">
                    <button className="tm-card-btn tm-card-btn--view"   onClick={() => handleShowViewModal(truck)}><FaEye /> View</button>
                    <button className="tm-card-btn tm-card-btn--edit"   onClick={() => { setEditingTruck(truck); setShowModal(true) }}><FaEdit /> Edit</button>
                    <button className="tm-card-btn tm-card-btn--delete" onClick={() => { setTruckToDelete(truck); setDeleteModal(true) }}><FaTrash /></button>
                  </div>
                </motion.div>
              </Col>
            ))}
          </AnimatePresence>
        </Row>
      )}

      {/* ADD / EDIT MODAL */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Formik
          initialValues={initialValues}
          validationSchema={TruckSchema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const submitData = { ...values, isGpsInstalled: values.isGpsInstalled === 'yes' }
              if (editingTruck) {
                await dispatch(updateTruck({ id: editingTruck.id, ...submitData })).unwrap()
              } else {
                await dispatch(addTruck(submitData)).unwrap()
              }
              handleCloseModal()
            } catch (err) {
              console.error('Failed to save truck:', err)
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({ errors, touched, isSubmitting, setFieldValue }) => (
            <FormikForm>
              <Modal.Header className="tm-modal-hero" as="div">
                <button type="button" className="tm-modal-close" onClick={handleCloseModal} aria-label="Close">✕</button>
                <div className="tm-modal-hero-icon">
                  {editingTruck ? <FaEdit /> : <FaTruck />}
                </div>
                <div className="tm-modal-hero-text">
                  <h2 className="tm-modal-hero-title">{editingTruck ? 'Edit Truck' : 'Add New Truck'}</h2>
                  <p className="tm-modal-hero-sub">
                    {editingTruck ? `Updating record for ${editingTruck.truckNumber}` : 'Enter vehicle details and document validity dates'}
                  </p>
                </div>
              </Modal.Header>
              <Modal.Body className="tm-modal-body">
                <div className="tm-form-section">
                  <div className="tm-form-section-title">Basic Information</div>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">Truck Number <span className="tm-required">*</span></label>
                        <Field name="truckNumber" className={`tm-input ${touched.truckNumber && errors.truckNumber ? 'tm-input--error' : ''}`} placeholder="e.g. MH 12 AB 1234" />
                        {touched.truckNumber && errors.truckNumber && <span className="tm-error">{errors.truckNumber}</span>}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label"><FaIdCard className="tm-lbl-icon" /> Chassis Number <span className="tm-required">*</span></label>
                        <Field name="chasisNumber" className={`tm-input ${touched.chasisNumber && errors.chasisNumber ? 'tm-input--error' : ''}`} placeholder="Enter chassis number" />
                        {touched.chasisNumber && errors.chasisNumber && <span className="tm-error">{errors.chasisNumber}</span>}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">Engine Number <span className="tm-required">*</span></label>
                        <Field name="engineNumber" className={`tm-input ${touched.engineNumber && errors.engineNumber ? 'tm-input--error' : ''}`} placeholder="Enter engine number" />
                        {touched.engineNumber && errors.engineNumber && <span className="tm-error">{errors.engineNumber}</span>}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label"><FaCalendarAlt className="tm-lbl-icon" /> Reg. Valid Upto <span className="tm-required">*</span></label>
                        <Field name="registrationValidUpto" type="date" className={`tm-input ${touched.registrationValidUpto && errors.registrationValidUpto ? 'tm-input--error' : ''}`} />
                        {touched.registrationValidUpto && errors.registrationValidUpto && <span className="tm-error">{errors.registrationValidUpto}</span>}
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="tm-form-group">
                        <label className="tm-label">GPS Installed? <span className="tm-required">*</span></label>
                        <div className="tm-radio-group">
                          <label className="tm-radio-label">
                            <Field type="radio" name="isGpsInstalled" value="yes" />
                            <span>Yes</span>
                          </label>
                          <label className="tm-radio-label">
                            <Field type="radio" name="isGpsInstalled" value="no" />
                            <span>No</span>
                          </label>
                        </div>
                        {touched.isGpsInstalled && errors.isGpsInstalled && <span className="tm-error">{errors.isGpsInstalled}</span>}
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="tm-form-group">
                        <label className="tm-label">RC Copy</label>
                        <label className="tm-file-label">
                          <FaUpload className="tm-file-icon" />
                          <span className="tm-file-text">Choose File</span>
                          <input type="file" className="tm-file-hidden" onChange={e => setFieldValue('fileRC', e.target.files[0])} />
                        </label>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="tm-form-section">
                  <div className="tm-form-section-title">Fitness</div>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">Valid From</label>
                        <Field name="fitnessValidFrom" type="date" className="tm-input" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">Valid To</label>
                        <Field name="fitnessValidTo" type="date" className="tm-input" />
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="tm-form-group">
                        <label className="tm-label">Fitness Certificate</label>
                        <label className="tm-file-label">
                          <FaUpload className="tm-file-icon" />
                          <span className="tm-file-text">Choose File</span>
                          <input type="file" className="tm-file-hidden" onChange={e => setFieldValue('fileFitness', e.target.files[0])} />
                        </label>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="tm-form-section">
                  <div className="tm-form-section-title">Insurance</div>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">Valid From</label>
                        <Field name="insuranceValidFrom" type="date" className="tm-input" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">Valid To</label>
                        <Field name="insuranceValidTo" type="date" className="tm-input" />
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="tm-form-group">
                        <label className="tm-label">Insurance Document</label>
                        <label className="tm-file-label">
                          <FaUpload className="tm-file-icon" />
                          <span className="tm-file-text">Choose File</span>
                          <input type="file" className="tm-file-hidden" onChange={e => setFieldValue('fileInsurance', e.target.files[0])} />
                        </label>
                      </div>
                    </Col>
                  </Row>
                </div>

                <div className="tm-form-section">
                  <div className="tm-form-section-title">PUC & Other</div>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">PUC Valid From</label>
                        <Field name="pucValidFrom" type="date" className="tm-input" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="tm-form-group">
                        <label className="tm-label">PUC Valid To</label>
                        <Field name="pucValidTo" type="date" className="tm-input" />
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="tm-form-group">
                        <label className="tm-label">PUC Copy</label>
                        <label className="tm-file-label">
                          <FaUpload className="tm-file-icon" />
                          <span className="tm-file-text">Choose File</span>
                          <input type="file" className="tm-file-hidden" onChange={e => setFieldValue('filePUC', e.target.files[0])} />
                        </label>
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="tm-form-group">
                        <label className="tm-label">Permit Copy</label>
                        <label className="tm-file-label">
                          <FaUpload className="tm-file-icon" />
                          <span className="tm-file-text">Choose File</span>
                          <input type="file" className="tm-file-hidden" onChange={e => setFieldValue('filePermit', e.target.files[0])} />
                        </label>
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="tm-form-group">
                        <label className="tm-label">TDS Declaration</label>
                        <label className="tm-file-label">
                          <FaUpload className="tm-file-icon" />
                          <span className="tm-file-text">Choose File</span>
                          <input type="file" className="tm-file-hidden" onChange={e => setFieldValue('fileTDS', e.target.files[0])} />
                        </label>
                      </div>
                    </Col>
                  </Row>
                </div>

              </Modal.Body>
              <Modal.Footer className="tm-modal-footer">
                <div className="tm-modal-footer-note"><span className="tm-required">*</span> Required field</div>
                <div className="tm-modal-footer-actions">
                  <button type="button" className="tm-btn-cancel" onClick={handleCloseModal} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="tm-btn-submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</>
                      : <>{editingTruck ? <><FaEdit className="me-2" />Update Truck</> : <><FaPlus className="me-2" />Save Truck</>}</>}
                  </button>
                </div>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      {/* VIEW MODAL */}
      <Modal show={!!viewTruck} onHide={handleCloseViewModal} centered>
        <Modal.Header closeButton className="tm-modal-header">
          <Modal.Title className="tm-modal-title">Truck Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tm-modal-body">
          {viewTruck && (
            <div className="tm-view-grid">
              <div className="tm-view-avatar"><FaTruck /></div>
              <div className="tm-view-name">{viewTruck.truckNumber}</div>
              <span className={`tm-badge ${viewTruck.isActive ? 'tm-badge--active' : 'tm-badge--inactive'}`}>
                {viewTruck.isActive ? 'Active' : 'Inactive'}
              </span>
              <div className="tm-view-rows">
                {[
                  { icon: <FaIdCard />,       label: 'Chassis',     val: viewTruck.chasisNumber },
                  { icon: <FaCar />,           label: 'Engine',      val: viewTruck.engineNumber },
                  { icon: <FaSatellite />,     label: 'GPS',         val: viewTruck.isGpsInstalled ? 'Installed' : 'Not Installed' },
                  { icon: <FaCalendarAlt />,   label: 'Reg. Upto',   val: viewTruck.registrationValidUpto ? new Date(viewTruck.registrationValidUpto).toLocaleDateString() : '—' },
                  { icon: <FaCalendarAlt />,   label: 'Fitness',     val: viewTruck.fitnessValidTo ? `Until ${new Date(viewTruck.fitnessValidTo).toLocaleDateString()}` : '—' },
                  { icon: <FaCalendarAlt />,   label: 'Insurance',   val: viewTruck.insuranceValidTo ? `Until ${new Date(viewTruck.insuranceValidTo).toLocaleDateString()}` : '—' },
                  { icon: <FaCalendarAlt />,   label: 'PUC',         val: viewTruck.pucValidTo ? `Until ${new Date(viewTruck.pucValidTo).toLocaleDateString()}` : '—' },
                ].map((row, i) => (
                  <div key={i} className="tm-view-row">
                    <span className="tm-view-icon">{row.icon}</span>
                    <div className="tm-view-detail">
                      <span className="tm-view-lbl">{row.label}</span>
                      <span className="tm-view-val">{row.val || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
        <Modal.Header closeButton className="tm-modal-header">
          <Modal.Title className="tm-modal-title">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tm-modal-body tm-delete-body">
          <div className="tm-delete-icon"><FaTrash /></div>
          <p className="tm-delete-msg">
            Are you sure you want to delete <strong>{truckToDelete?.truckNumber}</strong>?
            <br /><span className="tm-delete-warn">This action cannot be undone.</span>
          </p>
          <div className="tm-delete-actions">
            <button className="tm-btn-cancel" onClick={() => setDeleteModal(false)}>Cancel</button>
            <button className="tm-btn-delete" onClick={confirmDelete}>Delete</button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  )
}

export default TruckManagement
